const BRIDGE_BASE = 'https://bridge.a-to-mind.com';

function getSessionToken(): string | null {
  return localStorage.getItem('hold.session');
}

function getHumanKey(): string | null {
  return localStorage.getItem('hold.human_key');
}

interface ApiResponse<T> {
  data: T | null;
  error: string | null;
  status: number;
}

async function fetchWithAuth<T>(
  path: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  const session = getSessionToken();
  if (session) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${session}`;
  }

  try {
    const res = await fetch(`${BRIDGE_BASE}${path}`, {
      ...options,
      headers,
    });

    if (res.status === 204) {
      return { data: null, error: null, status: res.status };
    }

    const body = await res.json().catch(() => ({}));

    if (!res.ok) {
      return { data: null, error: body.error || `HTTP ${res.status}`, status: res.status };
    }

    return { data: body as T, error: null, status: res.status };
  } catch (e) {
    return { data: null, error: 'Network error', status: 0 };
  }
}

interface CaseResponse {
  ok: boolean;
  id: string;
  hold_id: string;
  deadlines: Record<string, string | null>;
}

interface CaseDetail {
  id: string;
  org_id: string;
  product_id: string;
  kind: string;
  title: string;
  summary: string | null;
  awareness_at: string;
  measure_at: string | null;
  notify_users: number;
  status: string;
  created_by: string;
  created_at: string;
}

interface HoldResponse {
  ok: boolean;
  hold_id: string;
  status: string;
}

interface HoldDetail {
  hold_id: string;
  kind: string;
  subject_type: string;
  subject_id: string;
  status: string;
  reason: string | null;
  case_id: string;
  created_by: string;
  decided_by: string | null;
  decided_at: string | null;
  expires_at: string;
  created_at: string;
}

interface AGRunResponse {
  ok: boolean;
  run_id: string;
  status: string;
}

interface AGRunDetail {
  run_id: string;
  case_id: string;
  org_id: string;
  assembly: string;
  interval_json: string;
  variant_json: string | null;
  mode: string;
  status: string;
  error: string | null;
  created_at: string;
}

interface AGScoreResponse {
  ok: boolean;
  status: string;
  mode: string;
  variants_scored: number;
  scores: Array<{
    score_id: string;
    run_id: string;
    chrom: string;
    pos: number;
    ref: string;
    alt: string;
    gene: string | null;
    avi_score: number;
    effect_json: string;
    created_at: string;
  }>;
  evidence: Array<{
    id: string;
    sha256: string;
    r2_key: string;
    bytes: number;
  }>;
}

export const api = {
  // Health
  health: () => fetchWithAuth<{ ok: boolean; service: string }>('/health'),

  // Cases
  getCase: (caseId: string) => fetchWithAuth<CaseDetail>(`/v1/cra/cases/${caseId}`),
  openCase: (productId: string, type: 'aev' | 'incident', awareAt: string) =>
    fetchWithAuth<CaseResponse>('/v1/cra/cases', {
      method: 'POST',
      body: JSON.stringify({ product_id: productId, type, aware_at: awareAt }),
    }),

  // Holds
  getHold: (holdId: string) => fetchWithAuth<HoldDetail>(`/v1/holds/${holdId}`),
  allowHold: (holdId: string, decision: 'allowed' | 'denied', reason?: string) => {
    const humanKey = getHumanKey();
    if (!humanKey) throw new Error('No human key set. Use "session set <human_key>" first.');
    return fetchWithAuth<HoldResponse>(`/v1/holds/${holdId}/allow`, {
      method: 'POST',
      headers: { 'x-hold-human-key': humanKey },
      body: JSON.stringify({ decision, reason }),
    });
  },

  // AlphaGenome
  createAGRun: (caseId: string, interval: string) =>
    fetchWithAuth<AGRunResponse>('/v1/ag/runs', {
      method: 'POST',
      body: JSON.stringify({ case_id: caseId, interval }),
    }),
  getAGRun: (runId: string) => fetchWithAuth<AGRunDetail>(`/v1/ag/runs/${runId}`),
  scoreAGRun: (runId: string) => fetchWithAuth<AGScoreResponse>(`/v1/ag/runs/${runId}/score`, { method: 'POST' }),
  getAGScores: (runId: string, limit = 100, offset = 0) =>
    fetchWithAuth<{ run_id: string; scores: AGScoreResponse['scores'] }>(`/v1/ag/runs/${runId}/scores?limit=${limit}&offset=${offset}`),

  // Evidence
  getEvidence: (objectId: string) => fetchWithAuth(`/v1/evidence/${objectId}`),

  // Session management
  setSessionToken: (token: string) => localStorage.setItem('hold.session', token),
  clearSession: () => localStorage.removeItem('hold.session'),
  setHumanKey: (key: string) => localStorage.setItem('hold.human_key', key),
  clearHumanKey: () => localStorage.removeItem('hold.human_key'),
  getSessionToken,
  getHumanKey,
};